import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FlowDatasourceEntity, type FlowDatasourceEntityType } from 'shared/copilot/flowDataAgentConstants';
import { areCirrusSourceStatesEqual, buildCirrusSourceState, type CirrusSourceOption, type CirrusSourceState, readAgentCirrusSource } from 'shared/copilot/cirrusSource';
import { type AlvieDatasourceOption, filterToSingleDatasource, resolveFlowDatasourceEntities } from 'shared/copilot/utils/alvieDatasourceOptions';
import { findCirrusSourceOption } from 'shared/copilot/utils/cirrusSourceOptions';
import { type PersistedDatasourceSelection, toFlowDatasourceEntityStub } from 'shared/copilot/utils/copilotSourceSelectionStorage';
interface AgentStateWriter {
  state: unknown;
  setState: (state: Record<string, unknown>) => void;
}
export interface DatasourceUserSelection {
  optionIds: string[];
  entities: FlowDatasourceEntity[];
  cirrusSource: CirrusSourceOption | null;
}
interface UseCopilotDatasourceSessionArgs {
  agent: AgentStateWriter;
  externalWorkspaceId: number;
  /** Picker clicks only — not persisted hydrates. */
  onUserSelectionChange?: (selection: DatasourceUserSelection) => void;
}

/** Exactly one kind of source can be selected at a time — Flow ignores `entities` when `cirrus_source` is set. */
interface DatasourceSelection {
  entities: FlowDatasourceEntity[];
  cirrusSource: CirrusSourceOption | null;
}
interface UseCopilotDatasourceSessionResult {
  selectedDatasourceIds: string[];
  selectedEntities: FlowDatasourceEntity[];
  selectedEntityTypes: FlowDatasourceEntityType[];
  selectedCirrusSource: CirrusSourceOption | null;
  hasSelectedDatasource: boolean;
  setSelectedDatasources: (optionIds: string[], datasourceOptions: AlvieDatasourceOption[]) => void;
  setSelectedCirrusSource: (option: CirrusSourceOption | null) => void;
  clearSelectedDatasource: (optionId?: string) => void;
  /** Replace picker + agent state from a persisted snapshot (chat hydrate / new-chat seed). */
  applyPersistedDatasource: (persisted: PersistedDatasourceSelection) => void;
  /**
   * Drop ids missing from live picker lists and rebuild Flow entities / cirrus
   * options from those lists. Does not treat this as a user click (no last-used write).
   */
  reconcileWithAvailableOptions: (datasourceOptions: AlvieDatasourceOption[], cirrusOptions: readonly CirrusSourceOption[]) => void;
  /** Re-apply the current selection to agent state (preserves unrelated metadata). */
  syncAgentDatasourceState: () => void;
}
const readAgentEntities = (agentState: unknown): FlowDatasourceEntity[] => {
  if (agentState == null || typeof agentState !== 'object') {
    return [];
  }
  const entities = (agentState as Record<string, unknown>).entities;
  return Array.isArray(entities) ? entities as FlowDatasourceEntity[] : [];
};
const areEntitiesEqual = (left: readonly FlowDatasourceEntity[], right: readonly FlowDatasourceEntity[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((entity, index) => {
    const other = right[index];
    return other?.id === entity.id && other?.type === entity.type;
  });
};

/**
 * Owns Copilot datasource picker React state and keeps it in sync with agent state.
 *
 * Why this exists: the picker UI and the agent both need the same datasource context.
 * The picker shows what the user has selected; agent state is what the backend agent
 * actually uses for tools and replies. If those diverge, the UI can look selected while
 * the agent has no datasource (or vice versa) — a confusing UX.
 *
 * Two kinds of source can be selected, and they are mutually exclusive:
 * - `entities` — a synced Flow entity (GA4 / Search Console account).
 * - `cirrus_source` — a Cirrus collection Flow queries directly, identified purely from
 *   Alvie's own database. Flow prefers `cirrus_source` and ignores `entities` when both
 *   are present, so only ever one of the two keys is written.
 *
 * `cirrus_source` is per-request, not sticky: it is re-written on every send, new chat
 * and chat load so each turn of the conversation carries it.
 *
 * This hook is the single source of truth for selection: picker changes write through
 * to agent state immediately, and an effect re-applies selection if agent state drifts
 * (e.g. after thread resets). Per-chat persistence lives in `useCopilotAgentConversation`
 * (localStorage hydrate on create/load).
 */
export const useCopilotDatasourceSession = ({
  agent,
  externalWorkspaceId,
  onUserSelectionChange
}: UseCopilotDatasourceSessionArgs): UseCopilotDatasourceSessionResult => {
  const onUserSelectionChangeRef = useRef(onUserSelectionChange);
  onUserSelectionChangeRef.current = onUserSelectionChange;
  // Local picker state — what the UI chip / composer shows as selected.
  const [selectedDatasourceIds, setSelectedDatasourceIds] = useState<string[]>([]);
  const [selectedEntities, setSelectedEntities] = useState<FlowDatasourceEntity[]>([]);
  const [selectedCirrusSource, setSelectedCirrusSourceState] = useState<CirrusSourceOption | null>(null);

  // Workspace id comes from the Alvie client and is independent of the picker.
  // Clearing the picker only clears the source — otherwise a turn without a
  // selected source would drop workspace context the agent still needs.
  const resolvedWorkspaceId = Number.isFinite(externalWorkspaceId) ? externalWorkspaceId : null;
  const applySelectionToAgentState = useCallback(({
    entities,
    cirrusSource
  }: DatasourceSelection) => {
    const currentState = agent.state != null && typeof agent.state === 'object' ? {
      ...(agent.state as Record<string, unknown>)
    } : {};

    // Flow validates `client_id` against the X-Alvie-Client-Id header and 400s on a
    // mismatch — send it so drift fails loudly instead of silently querying another client.
    const cirrusSourceState = buildCirrusSourceState(cirrusSource, resolvedWorkspaceId);
    if (cirrusSourceState != null) {
      // Precedence: never send both. `entities` is dropped, not emptied.
      const {
        entities: _droppedEntities,
        ...restState
      } = currentState;
      agent.setState({
        ...restState,
        cirrus_source: cirrusSourceState,
        external_workspace_id: resolvedWorkspaceId
      });
      return;
    }

    // Deselect by omitting `cirrus_source` entirely, not by sending a blank collection.
    const {
      cirrus_source: _droppedCirrusSource,
      ...restState_0
    } = currentState;
    agent.setState({
      ...restState_0,
      entities,
      external_workspace_id: resolvedWorkspaceId
    });
  }, [agent, resolvedWorkspaceId]);

  // Read the live selection without making every consumer depend on it changing.
  const selectionRef = useRef<DatasourceSelection>({
    entities: [],
    cirrusSource: null
  });
  selectionRef.current = {
    entities: selectedEntities,
    cirrusSource: selectedCirrusSource
  };
  const syncAgentDatasourceState = useCallback(() => {
    applySelectionToAgentState(selectionRef.current);
  }, [applySelectionToAgentState]);
  const expectedCirrusSourceState = useMemo<CirrusSourceState | null>(() => buildCirrusSourceState(selectedCirrusSource, resolvedWorkspaceId), [selectedCirrusSource, resolvedWorkspaceId]);

  // Re-apply picker → agent when agent.state drifts (new chat, load chat, remote reset).
  // Without this, the picker can still show a selection while agent state carries none.
  useEffect(() => {
    const agentCirrusSource = readAgentCirrusSource(agent.state);
    if (expectedCirrusSourceState != null) {
      if (areCirrusSourceStatesEqual(agentCirrusSource, expectedCirrusSourceState)) {
        return;
      }
    } else if (agentCirrusSource == null && areEntitiesEqual(readAgentEntities(agent.state), selectedEntities)) {
      return;
    }
    applySelectionToAgentState({
      entities: selectedEntities,
      cirrusSource: selectedCirrusSource
    });
  }, [agent.state, applySelectionToAgentState, expectedCirrusSourceState, selectedCirrusSource, selectedEntities]);
  const setSelectedDatasources = useCallback((optionIds: string[], datasourceOptions: AlvieDatasourceOption[]) => {
    const nextIds = filterToSingleDatasource(optionIds, datasourceOptions);
    const entities_0 = resolveFlowDatasourceEntities(nextIds, datasourceOptions);
    // Update UI and agent together so the next turn always sees the picker's choice.
    setSelectedDatasourceIds(nextIds);
    setSelectedEntities(entities_0);
    setSelectedCirrusSourceState(null);
    applySelectionToAgentState({
      entities: entities_0,
      cirrusSource: null
    });
    onUserSelectionChangeRef.current?.({
      optionIds: nextIds,
      entities: entities_0,
      cirrusSource: null
    });
  }, [applySelectionToAgentState]);
  const setSelectedCirrusSource = useCallback((option: CirrusSourceOption | null) => {
    setSelectedDatasourceIds(option == null ? [] : [option.id]);
    setSelectedEntities([]);
    setSelectedCirrusSourceState(option);
    applySelectionToAgentState({
      entities: [],
      cirrusSource: option
    });
    onUserSelectionChangeRef.current?.({
      optionIds: option == null ? [] : [option.id],
      entities: [],
      cirrusSource: option
    });
  }, [applySelectionToAgentState]);
  const clearSelectedDatasource = useCallback((optionId?: string) => {
    if (optionId == null || selectedCirrusSource?.id === optionId) {
      setSelectedDatasourceIds([]);
      setSelectedEntities([]);
      setSelectedCirrusSourceState(null);
      applySelectionToAgentState({
        entities: [],
        cirrusSource: null
      });
      onUserSelectionChangeRef.current?.({
        optionIds: [],
        entities: [],
        cirrusSource: null
      });
      return;
    }
    const removedIndex = selectedDatasourceIds.indexOf(optionId);
    const nextIds_0 = selectedDatasourceIds.filter(id => id !== optionId);
    const nextEntities = removedIndex === -1 ? selectedEntities : selectedEntities.filter((_, index) => index !== removedIndex);
    setSelectedDatasourceIds(nextIds_0);
    setSelectedEntities(nextEntities);
    applySelectionToAgentState({
      entities: nextEntities,
      cirrusSource: null
    });
    onUserSelectionChangeRef.current?.({
      optionIds: nextIds_0,
      entities: nextEntities,
      cirrusSource: null
    });
  }, [applySelectionToAgentState, selectedCirrusSource, selectedDatasourceIds, selectedEntities]);
  const applyPersistedDatasource = useCallback((persisted: PersistedDatasourceSelection) => {
    // Keep selectionRef in lockstep with the applied snapshot so any sync in the
    // same tick (before React re-renders) cannot resurrect the previous chat's pick.
    if (persisted.kind === 'cirrus') {
      selectionRef.current = {
        entities: [],
        cirrusSource: persisted.option
      };
      setSelectedDatasourceIds([persisted.option.id]);
      setSelectedEntities([]);
      setSelectedCirrusSourceState(persisted.option);
      applySelectionToAgentState({
        entities: [],
        cirrusSource: persisted.option
      });
      return;
    }
    if (persisted.kind === 'entities') {
      const entities_1 = persisted.entities.map(toFlowDatasourceEntityStub);
      selectionRef.current = {
        entities: entities_1,
        cirrusSource: null
      };
      setSelectedDatasourceIds(persisted.optionIds);
      setSelectedEntities(entities_1);
      setSelectedCirrusSourceState(null);
      applySelectionToAgentState({
        entities: entities_1,
        cirrusSource: null
      });
      return;
    }
    selectionRef.current = {
      entities: [],
      cirrusSource: null
    };
    setSelectedDatasourceIds([]);
    setSelectedEntities([]);
    setSelectedCirrusSourceState(null);
    applySelectionToAgentState({
      entities: [],
      cirrusSource: null
    });
  }, [applySelectionToAgentState]);
  const reconcileWithAvailableOptions = useCallback((datasourceOptions_0: AlvieDatasourceOption[], cirrusOptions: readonly CirrusSourceOption[]) => {
    if (selectedCirrusSource != null) {
      const live = findCirrusSourceOption(selectedCirrusSource.id, cirrusOptions);
      if (live == null) {
        selectionRef.current = {
          entities: [],
          cirrusSource: null
        };
        setSelectedDatasourceIds([]);
        setSelectedEntities([]);
        setSelectedCirrusSourceState(null);
        applySelectionToAgentState({
          entities: [],
          cirrusSource: null
        });
        return;
      }
      if (live.id === selectedCirrusSource.id && live.label === selectedCirrusSource.label && live.status === selectedCirrusSource.status && live.supported === selectedCirrusSource.supported) {
        return;
      }
      selectionRef.current = {
        entities: [],
        cirrusSource: live
      };
      setSelectedDatasourceIds([live.id]);
      setSelectedEntities([]);
      setSelectedCirrusSourceState(live);
      applySelectionToAgentState({
        entities: [],
        cirrusSource: live
      });
      return;
    }
    if (selectedDatasourceIds.length === 0) {
      return;
    }
    const optionIdSet = new Set(datasourceOptions_0.map(option_0 => option_0.id));
    const nextIds_1 = filterToSingleDatasource(selectedDatasourceIds.filter(id_0 => optionIdSet.has(id_0)), datasourceOptions_0);
    const entities_2 = resolveFlowDatasourceEntities(nextIds_1, datasourceOptions_0);
    const idsUnchanged = nextIds_1.length === selectedDatasourceIds.length && nextIds_1.every((id_1, index_0) => id_1 === selectedDatasourceIds[index_0]);
    // Persisted restore stores entities without `data`; rebuild once options exist.
    const alreadyResolved = idsUnchanged && entities_2.length === selectedEntities.length && selectedEntities.every(entity => Object.keys(entity.data).length > 0);
    if (alreadyResolved) {
      return;
    }
    selectionRef.current = {
      entities: entities_2,
      cirrusSource: null
    };
    setSelectedDatasourceIds(nextIds_1);
    setSelectedEntities(entities_2);
    setSelectedCirrusSourceState(null);
    applySelectionToAgentState({
      entities: entities_2,
      cirrusSource: null
    });
  }, [applySelectionToAgentState, selectedCirrusSource, selectedDatasourceIds, selectedEntities]);
  const selectedEntityTypes = useMemo(() => selectedEntities.map(entity_0 => entity_0.type), [selectedEntities]);
  return {
    selectedDatasourceIds,
    selectedEntities,
    selectedEntityTypes,
    selectedCirrusSource,
    hasSelectedDatasource: selectedEntities.length > 0 || selectedCirrusSource != null,
    setSelectedDatasources,
    setSelectedCirrusSource,
    clearSelectedDatasource,
    applyPersistedDatasource,
    reconcileWithAvailableOptions,
    syncAgentDatasourceState
  };
};